import { Select, SmallCircle, SuccessTag } from "../index";
import RangeBar from "../range-bar/RangeBar";
import { VerticalBar } from "../vertical-bar/VerticalBar";
import infoIcon from "../../assets/icons/inforound-icon.png";
import { useCreditReport } from "../../data/live";

const asArray = (value) => (Array.isArray(value) ? value : []);

const ratingInfo = [
  {
    category: "Excellent",
    range: "853 - 1200",
    color: "#1BBB6B",
    info: "A score in this range is generally considered excellent by Australian credit bureaus.",
  },
  {
    category: "Very Good",
    range: "735 - 852",
    color: "#7EBB1B",
    info: "A score in this range is generally considered very good.",
  },
  {
    category: "Good",
    range: "661 - 734",
    color: "#F2E103",
    info: "A score in this range is generally considered good.",
  },
  {
    category: "Average",
    range: "460 - 660",
    color: "#F69A37",
    info: "A score in this range is generally considered average.",
  },
];

const indications = [
  { name: "On Time", color: "#1BBB6B" },
  { name: "Missed Payment", color: "#F2E103" },
  { name: "Temporary Hardship Arrangement", color: "#F69A37" },
  { name: "Variation Hardship Arrangement", color: "#FC3C42" },
  { name: "No Data", color: "#F0F1F5" },
];

const CreditDetails = () => {
  const { profile, loading } = useCreditReport();
  const factors = profile?.factors || {};
  const scoreHistory = asArray(factors.scoreHistory);
  const paymentHistory = asArray(factors.paymentHistory);
  const averageScore = factors.averageScore;

  const infoBannerDetail = profile
    ? [
        {
          infoName: profile.rating || "Credit rating",
          info: profile.score != null
            ? `Current score: ${profile.score}`
            : "No score is present in the backend profile.",
        },
        {
          infoName: "Credit profile update",
          info: profile.pulledAt
            ? `Last pulled on ${new Date(profile.pulledAt).toLocaleDateString("en-AU")}`
            : "No provider pull date is present in the backend profile.",
        },
      ]
    : [];

  if (loading) {
    return <div className="p-5 text-center text-muted border rounded-3 mt-3">Loading credit details...</div>;
  }

  if (!profile) {
    return <div className="p-5 text-center text-muted border rounded-3 mt-3">No credit profile available.</div>;
  }

  return (
    <>
      <div className="row ">
        {profile.score != null && (
          <div className="col-12 mt-3">
            <div className="d-flex flex-column p-3 px-4 border rounded-3 gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fs-5 fw-medium">
                  See how your score stacks up
                </span>
                <Select className="rounded-pill w-auto" defaultValue="Country" />
              </div>

              <div className="d-flex justify-content-center align-items-center mt-4 ">
                <RangeBar
                  dataset={[
                    {
                      label: "You",
                      score: Number(profile.score),
                      labelColor: "#1bbb6b",
                      pointerScore: "",
                    },
                    ...(averageScore
                      ? [
                          {
                            label: "Average Credit Member",
                            score: Number(averageScore),
                            labelColor: "#303030",
                            pointerScore: "",
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </div>
          </div>
        )}

        <div className="col-12 mt-3">
          <div className="d-flex flex-column border rounded-3 gap-3">
            <div className="d-flex justify-content-between align-items-center p-3 px-4 border-bottom">
              <span className="fs-5 fw-medium">Score History</span>
              <div className="d-flex align-items-center gap-4 justify-content-end">
                <span className="fs14px">
                  My Score:{" "}
                  <span className="fw-medium" style={{ color: "#1bbb6b" }}>
                    {profile.score ?? "Not set"}
                  </span>
                </span>

                <span className="fs14px">
                  Status:{" "}
                  <SuccessTag tagName={profile.rating || "AVAILABLE"} className={"rounded-2"} />
                </span>

                <Select defaultValue="ALL" className="rounded-pill w-25" />
              </div>
            </div>

            <div className="p-3 px-4">
              {scoreHistory.length ? (
                <VerticalBar
                  label={scoreHistory.map((item) => item.label || item.month)}
                  legendSize={12}
                  barChartData={[
                    {
                      label: "You",
                      data: scoreHistory.map((item) => Number(item.score || 0)),
                      backgroundColor: "#1BBB6B",
                      borderRadius: 4,
                      borderSkipped: false,
                    },
                  ]}
                  displayLegends={false}
                  stepSize={200}
                />
              ) : (
                <div className="p-5 text-center text-muted">No score history available.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 mt-3 d-flex flex-column gap-3">
          {infoBannerDetail.map(({ infoName, info }) => (
            <div
              key={infoName}
              className="d-flex gap-3 align-item-center p-3 rounded-3"
              style={{ backgroundColor: "#F7F7F7" }}
            >
              <div className="d-flex align-items-center ">
                <img src={infoIcon} alt="info" />
              </div>

              <div className="d-flex flex-column gap-1">
                <span className="fw-semibold fs-6">{infoName}</span>
                <span className="fs12px" style={{ color: "#1E344E" }}>
                  {info}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="col-12 mt-3">
          <div className="d-flex flex-column gap-3 border rounded-3 p-3 px-4">
            {ratingInfo.map(({ category, info, range, color }) => (
              <div key={category} className="d-flex flex-column gap-1">
                <span className="fw-semibold">
                  {category} : <span style={{ color }}>{range}</span>
                </span>
                <span className="fs12px" style={{ color: "#7d7d7d" }}>
                  {info}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="col-12 mt-3">
          <div className="d-flex flex-column gap-4 p-3 px-4 rounded-3 border">
            <div className="d-flex flex-column gap-1">
              <span className="fs-5 fw-medium">Payment History</span>
              <span className="fs12px" style={{ color: "#7d7d7d" }}>
                Payment history is shown only when returned by the credit
                provider profile.
              </span>
            </div>

            {paymentHistory.length ? (
              <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-md-5 gap-3 flex-wrap">
                {paymentHistory.map(({ history, year }) => (
                  <div
                    key={year}
                    className="d-flex flex-column gap-2"
                    style={{ width: "212px" }}
                  >
                    <div className="d-flex align-items-center gap-1 flex-wrap ">
                      {asArray(history).map((item, key) => (
                        <SmallCircle
                          key={`${year}-${key}`}
                          color={item.color || "#F0F1F5"}
                          size="32px"
                        />
                      ))}
                    </div>
                    <span className="fs14px fw-medium text-center">{year}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-5 text-center text-muted">No payment history available.</div>
            )}

            <div className="d-flex align-items-center gap-5 justify-content-center flex-wrap">
              {indications.map(({ name, color }) => (
                <div key={name} className="d-flex gap-2 align-items-center">
                  <SmallCircle color={color} size="11px" />
                  <span className="fs14px fw-medium">{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreditDetails;
